import { neighborhoodCrudConfig, type Neighborhood } from '@/config/objects/neighborhoods';
import { CrudListView, type CrudConfig } from '@startupquickstart/frontend';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface NeighborhoodsListProps {
  /**
   * Optional custom config (with modified endpoints, actions, etc.)
   */
  config?: CrudConfig<Neighborhood>;
  /**
   * Optional default where clause for filtering
   */
  defaultWhere?: Record<string, unknown>;
  /**
   * Optional custom onItemClick handler
   */
  onItemClick?: (neighborhood: Neighborhood) => void;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Base URL for navigation, defaults to /neighborhoods
   */
  baseUrl?: string;
}

/**
 * Reusable neighborhoods list component
 * Pass a custom config with modified actions to customize behavior
 */
export function NeighborhoodsList({
  config,
  baseUrl = '/neighborhoods',
  defaultWhere,
  onItemClick,
  className,
}: NeighborhoodsListProps) {
  const navigate = useNavigate();

  const _onItemClick = useCallback(
    (neighborhood: Neighborhood) => {
      if (onItemClick) {
        onItemClick(neighborhood);
      } else {
        navigate(`/neighborhoods/${neighborhood.id}`);
      }
    },
    [navigate, onItemClick],
  );

  return (
    <div className={className}>
      <CrudListView
        config={config || neighborhoodCrudConfig}
        baseUrl={baseUrl}
        onItemClick={_onItemClick}
        defaultWhere={defaultWhere}
        getValueFromItem={(neighborhood: Neighborhood) => neighborhood.id}
        getLabelFromItem={(neighborhood: Neighborhood) => neighborhood.name}
      />
    </div>
  );
}
